import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Twitter, Instagram, Linkedin, Facebook } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const location = useLocation();
  const aboutMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (aboutMenuRef.current && !aboutMenuRef.current.contains(event.target as Node)) {
        setIsAboutOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { 
      name: 'Home', 
      path: '/',
    },
    { 
      name: 'About Us', 
      path: '/about',
      subItems: [
        { name: 'About Us', path: '/about' }
      ]
    },
    { 
      name: 'Services', 
      path: '/services',
    },
    { 
      name: 'Clientele',
      path: '/clientele',
    },
    { 
      name: 'Sustainability', 
      path: '/sustainability',
    },
    { 
      name: 'Gallery', 
      path: '/gallery',
    },
    { 
      name: 'Contact', 
      path: '/contact',
    },
  ];

  return (
    <div className="sticky top-0 z-50">
      {/* Top Bar with Contact Info */}
      <div className="bg-[#295cab] text-white text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 py-1.5 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-1 md:space-x-4 mb-1 md:mb-0">
            <a href="tel:+256393254458" className="flex items-center hover:underline">
              <Phone className="w-3 h-3 md:w-4 md:h-4 mr-1" />
              <span>+256 393 254458</span>
            </a>
            <span className="hidden md:inline-block">|</span>
            <a href="mailto:info@biohazwaste.com" className="flex items-center hover:underline">
              <span>info@biohazwaste.com</span>
            </a>
          </div>
          <div className="flex items-center space-x-3 md:space-x-4">
            <a href="https://twitter.com/biohazwaste" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-200 transition-colors">
              <Twitter className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </a>
            <a href="https://facebook.com/biohazwaste" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-200 transition-colors">
              <Facebook className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </a>
            <a href="https://instagram.com/biohazwaste" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-200 transition-colors">
              <Instagram className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </a>
            <a href="https://linkedin.com/company/biohazwaste" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-200 transition-colors">
              <Linkedin className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="w-full bg-white shadow-lg py-1">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 flex flex-row-reverse xl:flex-row items-center justify-between h-14 relative">
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img 
              src="/logo.png" 
              alt="BioHaz Waste Solutions Logo" 
              className="h-10 w-auto hover:opacity-90 transition-opacity duration-300" 
            />
          </Link>

          {/* Hamburger Button for Mobile */}
          <button
            className="xl:hidden flex items-center justify-center p-3 mr-2 rounded-md text-gray-700 hover:text-[#295cab] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#295cab] transition-colors z-50"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <X className="w-7 h-7" />
            ) : (
              <Menu className="w-7 h-7" />
            )}
          </button>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center justify-center flex-1 px-8">
            <div className="flex items-center space-x-6 2xl:space-x-8">
              {navItems.map((item, index) => (
                <div key={item.name} className="relative group" ref={item.name === 'About Us' ? aboutMenuRef : null}>
                  {item.subItems ? (
                    <div className="relative">
                      <button
                        onClick={() => setIsAboutOpen(!isAboutOpen)}
                        className={`flex items-center space-x-1 font-medium transition-all duration-300 text-sm lg:text-base ${
                          item.subItems.some(subItem => location.pathname === subItem.path)
                            ? 'text-[#295cab]'
                            : 'text-gray-700 hover:text-[#295cab]'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isAboutOpen ? 'transform rotate-180' : ''}`} />
                      </button>
                      <div 
                        className={`absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 transition-all duration-200 ${isAboutOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                      >
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-4 py-2 text-sm ${
                              location.pathname === subItem.path
                                ? 'bg-blue-50 text-[#295cab]'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-[#295cab]'
                            }`}
                            onClick={() => setIsAboutOpen(false)}
                          >
                            <span>{subItem.name}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center font-medium transition-all duration-300 text-sm lg:text-base relative group ${
                        location.pathname === item.path
                          ? 'text-[#295cab]'
                          : 'text-gray-700 hover:text-[#295cab]'
                      }`}
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <span>{item.name}</span>
                      <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#295cab] transition-all duration-300 group-hover:w-full ${
                        location.pathname === item.path ? 'w-full' : ''
                      }`}></span>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Call to Action Button - Desktop */}
          <div className="hidden xl:flex items-center space-x-2">
            <a 
              href="tel:+256393254458" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </nav>
      
      {/* Mobile Navigation */}
      {/* Mobile Slide-in Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 xl:hidden transition-all duration-300 ${isOpen ? 'visible opacity-100' : 'invisible opacity-0 pointer-events-none'}`}
        aria-hidden={!isOpen}
        tabIndex={isOpen ? 0 : -1}
        onClick={() => setIsOpen(false)}
        style={{ background: isOpen ? 'rgba(0,0,0,0.45)' : 'transparent' }}
      >
        {/* Slide-in Panel */}
        <nav
          className={`absolute top-0 right-0 h-full w-4/5 max-w-xs bg-white shadow-xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          onClick={e => e.stopPropagation()}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-md text-gray-700 hover:text-[#295cab] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#295cab] z-50"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-7 h-7" />
          </button>
          <div className="flex flex-col pt-20 pb-8 px-6 space-y-2">
            {navItems.map((item, index) => (
              <div key={item.name} className="space-y-1">
                {item.subItems ? (
                  <div className="space-y-1">
                    <button
                      onClick={() => setIsAboutOpen(!isAboutOpen)}
                      className={`w-full flex items-center px-4 py-4 rounded-md text-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                        item.subItems.some(subItem => location.pathname === subItem.path)
                          ? 'text-[#295cab] bg-blue-50'
                          : 'text-gray-700 hover:text-[#295cab] hover:bg-blue-50'
                      }`}
                      aria-expanded={isAboutOpen}
                      aria-controls="mobile-about-menu"
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-5 h-5 ml-2 transition-transform duration-200 ${isAboutOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div 
                      id="mobile-about-menu" 
                      className={`pl-4 space-y-1 overflow-hidden transition-all duration-300 ${isAboutOpen ? 'max-h-48' : 'max-h-0'}`}
                      aria-hidden={!isAboutOpen}
                    >
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => {
                            setIsOpen(false);
                            setIsAboutOpen(false);
                          }}
                          className={`block px-7 py-3 rounded-md text-base font-medium transition-all duration-300 transform hover:scale-105 ${
                            location.pathname === subItem.path
                              ? 'text-[#295cab] bg-blue-50'
                              : 'text-gray-700 hover:text-[#295cab] hover:bg-blue-50'
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center px-4 py-4 rounded-md text-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                      location.pathname === item.path
                        ? 'text-[#295cab] bg-blue-50'
                        : 'text-gray-700 hover:text-[#295cab] hover:bg-blue-50'
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <a
              href="tel:+256393254458"
              className="flex items-center space-x-2 bg-emerald-600 text-white px-4 py-4 rounded-md hover:bg-emerald-700 transition-all duration-300 mt-4 btn-animate hover-glow text-lg font-semibold"
            >
              <Phone className="w-5 h-5 animate-pulse" />
              <span>Emergency Hotline</span>
            </a>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Navigation;